<template>
  <div class="flex-subscriptions-container">
    <!-- Section Title -->
    <h3 class="section-title">Subscription Offers</h3>

    <!-- Currency Tabs -->
    <div class="currency-tabs">
      <button
        v-for="currency in currencies"
        :key="currency"
        :class="['currency-tab', activeCurrency === currency && 'active']"
        @click="activeCurrency = currency"
      >
        {{ currency.toUpperCase() }}
      </button>
      <div class="tab-actions">
        <button class="sync-all-btn" @click="syncAll" :disabled="syncing">
          🗘
          Sync All
        </button>
        <button class="add-btn" @click="startCreate">+ New Offer</button>
      </div>
    </div>

    <!-- Offers List -->
    <div class="offers-list">
      <!-- New Offer Form -->
      <transition name="slide-fade">
        <div v-if="isCreating && newOffer" class="offer-card editing">
          <div class="card-content">
            <div class="form-section">
              <h4>Stripe Product</h4>
              <div class="stripe-selector">
                <select v-model="selectedProductId" class="product-select" @change="onProductChange">
                  <option value="">Select a product...</option>
                  <option v-for="product in stripeProducts" :key="product.id" :value="product.id">
                    {{ product.name }}
                  </option>
                </select>
                <select v-model="newOffer.external_price_id" class="price-select" :disabled="!selectedProductId">
                  <option value="">Select a price...</option>
                  <option v-for="price in availablePrices" :key="price.id" :value="price.id">
                    {{ formatPrice(price.unit_amount, price.currency) }} / {{ price.interval }}
                  </option>
                </select>
                <button class="btn-refresh" @click="loadStripeProducts" title="Refresh products">
                  🗘
                </button>
              </div>
            </div>

            <div class="form-section">
              <h4>Offer Details</h4>
              <div class="form-row">
                <div class="form-group flex-2">
                  <label>Title *</label>
                  <input
                    v-model="newOffer.title"
                    type="text"
                    placeholder="e.g., Annual Subscription"
                    class="text-input"
                    ref="newOfferTitle"
                  />
                </div>
                <div class="form-group">
                  <label>Priority</label>
                  <input
                    v-model.number="newOffer.priority"
                    type="number"
                    min="0"
                    class="number-input"
                  />
                </div>
                <div class="form-group">
                  <label>Free Trial Days</label>
                  <input
                    v-model.number="newOffer.free_trial_days"
                    type="number"
                    min="0"
                    class="number-input"
                  />
                </div>
              </div>
              <div class="form-group">
                <label>Description</label>
                <div class="rich-editor-toolbar">
                  <button type="button" @click="execCommand('bold')" title="Bold"><b>B</b></button>
                  <button type="button" @click="execCommand('italic')" title="Italic"><i>I</i></button>
                  <button type="button" @click="execCommand('underline')" title="Underline"><u>U</u></button>
                  <button type="button" @click="execCommand('insertUnorderedList')" title="Bullet List">☰</button>
                </div>
                <div
                  ref="newDescriptionEditor"
                  class="rich-editor"
                  contenteditable="true"
                  @input="newOffer.description = $event.target.innerHTML"
                  @paste="onPaste"
                ></div>
              </div>
            </div>

            <div class="card-actions">
              <button class="btn-cancel" @click="cancelCreate">Cancel</button>
              <button class="btn-save" @click="saveOffer">Create</button>
            </div>
          </div>
        </div>
      </transition>

      <!-- Existing Offers -->
      <transition-group name="list" tag="div">
        <div
          v-for="offer in filteredOffers"
          :key="offer.id"
          :class="['offer-card', editingId === offer.id ? 'editing' : 'viewable']"
        >
          <!-- View Mode -->
          <div v-if="editingId !== offer.id" class="card-content" @click="startEdit(offer)">
            <div class="offer-header">
              <div class="offer-title">
                {{ offer.title }}
                <span :class="['badge', offer.active ? 'active' : 'inactive']">
                  {{ offer.active ? 'Active' : 'Inactive' }}
                </span>
              </div>
              <div class="offer-price">
                {{ formatPrice(offer.remote_price, offer.remote_currency) }}
                <span class="price-interval">/ {{ offer.remote_interval }}</span>
              </div>
            </div>
            <div class="offer-meta">
              <span v-if="offer.free_trial_days > 0" class="trial-badge">{{ offer.free_trial_days }} day trial</span>
              <span class="priority">Priority: {{ offer.priority }}</span>
              <span v-if="offer.remote_last_sync" class="last-sync">Synced: {{ formatDate(offer.remote_last_sync) }}</span>
            </div>
            <div v-if="offer.description" class="offer-description" v-html="offer.description"></div>
            <div class="card-actions" @click.stop>
              <button class="btn-sync" @click="syncOffer(offer)" title="Sync from Stripe">
                🗘
              </button>
              <button v-if="!offer.active" class="btn-activate" @click="activateOffer(offer)">Activate</button>
              <button v-else class="btn-deactivate" @click="deactivateOffer(offer)">Deactivate</button>
              <button class="btn-delete" @click="confirmDelete(offer)">Delete</button>
            </div>
          </div>

          <!-- Edit Mode -->
          <div v-else class="card-content" @click.stop>
            <div class="form-section">
              <h4>Stripe Product</h4>
              <div class="stripe-selector">
                <select v-model="editSelectedProductId" class="product-select" @change="onEditProductChange">
                  <option value="">Select a product...</option>
                  <option v-for="product in stripeProducts" :key="product.id" :value="product.id">
                    {{ product.name }}
                  </option>
                </select>
                <select v-model="editingOffer.external_price_id" class="price-select" :disabled="!editSelectedProductId">
                  <option value="">Select a price...</option>
                  <option v-for="price in editAvailablePrices" :key="price.id" :value="price.id">
                    {{ formatPrice(price.unit_amount, price.currency) }} / {{ price.interval }}
                  </option>
                </select>
              </div>
            </div>

            <div class="form-section">
              <h4>Offer Details</h4>
              <div class="form-row">
                <div class="form-group flex-2">
                  <label>Title *</label>
                  <input
                    v-model="editingOffer.title"
                    type="text"
                    class="text-input"
                    ref="editOfferTitle"
                  />
                </div>
                <div class="form-group">
                  <label>Priority</label>
                  <input
                    v-model.number="editingOffer.priority"
                    type="number"
                    min="0"
                    class="number-input"
                  />
                </div>
                <div class="form-group">
                  <label>Free Trial Days</label>
                  <input
                    v-model.number="editingOffer.free_trial_days"
                    type="number"
                    min="0"
                    class="number-input"
                  />
                </div>
              </div>
              <div class="form-group">
                <label>Description</label>
                <div class="rich-editor-toolbar">
                  <button type="button" @click="execCommand('bold')" title="Bold"><b>B</b></button>
                  <button type="button" @click="execCommand('italic')" title="Italic"><i>I</i></button>
                  <button type="button" @click="execCommand('underline')" title="Underline"><u>U</u></button>
                  <button type="button" @click="execCommand('insertUnorderedList')" title="Bullet List">☰</button>
                </div>
                <div
                  ref="editDescriptionEditor"
                  class="rich-editor"
                  contenteditable="true"
                  @input="editingOffer.description = $event.target.innerHTML"
                  @paste="onPaste"
                ></div>
              </div>
            </div>

            <div class="card-actions">
              <button type="button" class="btn-cancel" @click.stop="cancelEdit">Cancel</button>
              <button type="button" class="btn-save" @click.stop="updateOffer">Save</button>
            </div>
          </div>
        </div>
      </transition-group>

      <!-- Empty State -->
      <div v-if="!isCreating && filteredOffers.length === 0" class="empty-state">
        <p>No offers for {{ activeCurrency.toUpperCase() }} yet</p>
        <button class="add-btn" @click="startCreate">+ Create First Offer</button>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <transition name="modal-fade">
      <div v-if="deletingOffer" class="modal-overlay" @click.self="deletingOffer = null">
        <div class="confirm-modal">
          <h3>Delete Offer</h3>
          <p>Are you sure you want to delete <strong>{{ deletingOffer.title }}</strong>?</p>
          <p class="warning">This action cannot be undone.</p>
          <div class="modal-actions">
            <button class="btn-cancel" @click="deletingOffer = null">Cancel</button>
            <button class="btn-delete" @click="deleteOffer">Delete</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  data() {
    return {
      // Currencies
      currencies: ['usd'],
      activeCurrency: 'usd',

      // Offers
      offers: [],

      // Stripe products
      stripeProducts: [],
      selectedProductId: '',
      editSelectedProductId: '',

      // Create mode
      isCreating: false,
      newOffer: null,

      // Edit mode
      editingId: null,
      editingOffer: null,

      // Delete confirmation
      deletingOffer: null,

      // Syncing state
      syncing: false
    }
  },

  computed: {
    filteredOffers() {
      return this.offers
        .filter(offer => offer.currency === this.activeCurrency)
        .sort((a, b) => b.priority - a.priority)
    },

    availablePrices() {
      if (!this.selectedProductId) return []
      const product = this.stripeProducts.find(p => p.id === this.selectedProductId)
      if (!product) return []
      return product.prices.filter(p => p.currency === this.activeCurrency)
    },

    editAvailablePrices() {
      if (!this.editSelectedProductId) return []
      const product = this.stripeProducts.find(p => p.id === this.editSelectedProductId)
      if (!product) return []
      return product.prices.filter(p => p.currency === this.activeCurrency)
    }
  },

  methods: {
    // Load data
    loadCurrencies() {
      fetch('/admin/api/v2/config/currencies')
        .then(resp => {
          if (resp.status === 200) return resp.json()
          if (resp.status === 404) return null
          throw Error(resp.statusText)
        })
        .then(resp => {
          if (resp && resp.data && Array.isArray(resp.data.value)) {
            this.currencies = resp.data.value.map(c => c.toLowerCase())
            if (this.currencies.length > 0 && !this.currencies.includes(this.activeCurrency)) {
              this.activeCurrency = this.currencies[0]
            }
          }
        })
        .catch(() => {
          // Use defaults
        })
    },

    loadOffers() {
      fetch('/admin/api/v2/flex-subscriptions/offers')
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(resp => {
          this.offers = resp.data || []
        })
        .catch(() => {
          this.$root.app.showAlert('Error loading subscription offers', 'danger')
        })
    },

    loadStripeProducts() {
      fetch(`/admin/api/v2/flex-subscriptions/offers/stripe-products?currency=${this.activeCurrency}`)
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(resp => {
          this.stripeProducts = resp.data || []
        })
        .catch(() => {
          this.$root.app.showAlert('Error loading Stripe products', 'danger')
        })
    },

    // Helpers
    emptyOffer() {
      return {
        external_product_id: '',
        external_price_id: '',
        title: '',
        description: '',
        priority: 0,
        currency: this.activeCurrency,
        active: true,
        free_trial_days: 0
      }
    },

    formatPrice(cents, currency) {
      if (!cents) return '0.00'
      const amount = cents / 100
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: (currency || 'usd').toUpperCase()
      }).format(amount)
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    execCommand(command) {
      document.execCommand(command, false, null)
    },

    onPaste(e) {
      e.preventDefault()
      const text = e.clipboardData.getData('text/plain')
      document.execCommand('insertText', false, text)
    },

    onProductChange() {
      const product = this.stripeProducts.find(p => p.id === this.selectedProductId)
      if (product) {
        this.newOffer.external_product_id = product.id
        this.newOffer.external_price_id = ''
      }
    },

    onEditProductChange() {
      const product = this.stripeProducts.find(p => p.id === this.editSelectedProductId)
      if (product) {
        this.editingOffer.external_product_id = product.id
        this.editingOffer.external_price_id = ''
      }
    },

    // Create
    startCreate() {
      this.cancelEdit()
      this.isCreating = true
      this.newOffer = this.emptyOffer()
      this.selectedProductId = ''
      this.loadStripeProducts()
      this.$nextTick(() => {
        if (this.$refs.newDescriptionEditor) {
          this.$refs.newDescriptionEditor.innerHTML = ''
        }
      })
    },

    cancelCreate() {
      this.isCreating = false
      this.newOffer = null
      this.selectedProductId = ''
    },

    saveOffer() {
      if (!this.newOffer.title.trim()) {
        this.$root.app.showAlert('Please enter a title', 'warning')
        return
      }
      if (!this.newOffer.external_product_id || !this.newOffer.external_price_id) {
        this.$root.app.showAlert('Please select a Stripe product and price', 'warning')
        return
      }

      fetch('/admin/api/v2/flex-subscriptions/offers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: this.newOffer })
      })
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(() => {
          this.$root.app.showAlert('Offer created successfully', 'success')
          this.cancelCreate()
          this.loadOffers()
        })
        .catch(() => {
          this.$root.app.showAlert('Error creating offer', 'danger')
        })
    },

    // Edit
    startEdit(offer) {
      this.cancelCreate()
      this.editingId = offer.id
      this.editingOffer = { ...offer }
      this.editSelectedProductId = offer.external_product_id
      this.loadStripeProducts()
      this.$nextTick(() => {
        setTimeout(() => {
          const editor = this.$refs.editDescriptionEditor
          if (editor) {
            const el = Array.isArray(editor) ? editor[0] : editor
            if (el) el.innerHTML = offer.description || ''
          }
        }, 50)
      })
    },

    cancelEdit() {
      this.editingId = null
      this.editingOffer = null
      this.editSelectedProductId = ''
    },

    updateOffer() {
      if (!this.editingOffer.title.trim()) {
        this.$root.app.showAlert('Please enter a title', 'warning')
        return
      }

      const data = {
        external_product_id: this.editingOffer.external_product_id,
        external_price_id: this.editingOffer.external_price_id,
        title: this.editingOffer.title,
        description: this.editingOffer.description,
        priority: this.editingOffer.priority,
        free_trial_days: this.editingOffer.free_trial_days
      }

      fetch(`/admin/api/v2/flex-subscriptions/offers/${this.editingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data })
      })
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(() => {
          this.$root.app.showAlert('Offer updated successfully', 'success')
          this.cancelEdit()
          this.loadOffers()
        })
        .catch(() => {
          this.$root.app.showAlert('Error updating offer', 'danger')
        })
    },

    // Delete
    confirmDelete(offer) {
      this.deletingOffer = offer
    },

    deleteOffer() {
      fetch(`/admin/api/v2/flex-subscriptions/offers/${this.deletingOffer.id}`, { method: 'DELETE' })
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(() => {
          this.$root.app.showAlert('Offer deleted successfully', 'success')
          this.deletingOffer = null
          this.loadOffers()
        })
        .catch(() => {
          this.$root.app.showAlert('Error deleting offer', 'danger')
        })
    },

    // Activate/Deactivate
    activateOffer(offer) {
      fetch(`/admin/api/v2/flex-subscriptions/offers/${offer.id}/activate`, { method: 'POST' })
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(() => {
          this.$root.app.showAlert('Offer activated', 'success')
          this.loadOffers()
        })
        .catch(() => {
          this.$root.app.showAlert('Error activating offer', 'danger')
        })
    },

    deactivateOffer(offer) {
      fetch(`/admin/api/v2/flex-subscriptions/offers/${offer.id}/deactivate`, { method: 'POST' })
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(() => {
          this.$root.app.showAlert('Offer deactivated', 'success')
          this.loadOffers()
        })
        .catch(() => {
          this.$root.app.showAlert('Error deactivating offer', 'danger')
        })
    },

    // Sync
    syncOffer(offer) {
      fetch(`/admin/api/v2/flex-subscriptions/offers/${offer.id}/sync`, { method: 'POST' })
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(() => {
          this.$root.app.showAlert('Offer synced', 'success')
          this.loadOffers()
        })
        .catch(() => {
          this.$root.app.showAlert('Error syncing offer', 'danger')
        })
    },

    syncAll() {
      this.syncing = true
      fetch('/admin/api/v2/flex-subscriptions/offers/sync-all', { method: 'POST' })
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(resp => {
          this.$root.app.showAlert(`Synced ${resp.data?.synced || 0} offers`, 'success')
          this.loadOffers()
          this.syncing = false
        })
        .catch(() => {
          this.$root.app.showAlert('Error syncing offers', 'danger')
          this.syncing = false
        })
    }
  },

  watch: {
    activeCurrency() {
      if (this.isCreating) {
        this.newOffer.currency = this.activeCurrency
        this.loadStripeProducts()
      }
      if (this.editingId) {
        this.loadStripeProducts()
      }
    }
  },

  mounted() {
    this.$root.app.setActivePageTitle('Subscription Offers', false)
    this.$root.$emit('set-toolbar', 'empty-toolbar')
    this.loadCurrencies()
    this.loadOffers()
  }
}
</script>

<style scoped>
.flex-subscriptions-container {
  padding: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
}

/* Section titles */
.section-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.75rem;
}

/* Currency Tabs */
.currency-tabs {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.currency-tab {
  padding: 0.5rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
  color: #94A3B8;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  transition: all 0.2s;
}

.currency-tab:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

.currency-tab.active {
  background: linear-gradient(135deg, #20B4FF 0%, #007BFF 100%);
  border-color: transparent;
  color: #fff;
  box-shadow: 0 2px 8px rgba(32, 180, 255, 0.3);
}

.tab-actions {
  margin-left: auto;
  display: flex;
  gap: 0.75rem;
}

.sync-all-btn {
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #94A3B8;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.sync-all-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.sync-all-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.add-btn {
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #20B4FF 0%, #007BFF 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.9rem;
  transition: all 0.2s;
  box-shadow: 0 4px 6px rgba(32, 180, 255, 0.2);
}

.add-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 12px rgba(32, 180, 255, 0.3);
}

/* Offers List */
.offers-list {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

/* Offer Card */
.offer-card {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  overflow: hidden;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: 1rem;
}

.offer-card.viewable {
  cursor: pointer;
}

.offer-card.viewable:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.1);
  transform: translateX(4px);
}

.offer-card.editing {
  background: rgba(255, 255, 255, 0.08);
  border-color: #20B4FF;
  box-shadow: 0 0 0 1px #20B4FF, 0 10px 25px rgba(0, 0, 0, 0.2);
}

.card-content {
  padding: 1.25rem 1.5rem;
}

/* Offer Header */
.offer-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
}

.offer-title {
  font-weight: 600;
  font-size: 1.1rem;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.offer-price {
  font-size: 1.25rem;
  font-weight: 700;
  color: #20B4FF;
}

.price-interval {
  font-size: 0.85rem;
  font-weight: 400;
  color: #94A3B8;
}

.badge {
  font-size: 0.7rem;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
}

.badge.active {
  background: rgba(40, 167, 69, 0.15);
  color: #28a745;
  border: 1px solid rgba(40, 167, 69, 0.2);
}

.badge.inactive {
  background: rgba(108, 117, 125, 0.15);
  color: #94A3B8;
  border: 1px solid rgba(108, 117, 125, 0.2);
}

/* Offer Meta */
.offer-meta {
  display: flex;
  gap: 1rem;
  font-size: 0.8rem;
  color: #94A3B8;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
}

.trial-badge {
  background: rgba(32, 180, 255, 0.15);
  color: #20B4FF;
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  font-weight: 500;
}

/* Offer Description */
.offer-description {
  font-size: 0.9rem;
  color: #c9d1d9;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  line-height: 1.5;
}

.offer-description :deep(ul) {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

.offer-description :deep(p) {
  margin: 0.25rem 0;
}

/* Form Sections */
.form-section {
  margin-bottom: 1.5rem;
}

.form-section h4 {
  font-size: 0.8rem;
  font-weight: 600;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.75rem;
}

.stripe-selector {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.product-select,
.price-select {
  flex: 1;
  min-width: 200px;
  padding: 0.5rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  background: #1a1d23;
  color: #fff;
  font-size: 0.9rem;
}

.product-select option,
.price-select option {
  background: #1a1d23;
  color: #fff;
}

.product-select:focus,
.price-select:focus {
  outline: none;
  border-color: #20B4FF;
}

.product-select:disabled,
.price-select:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-refresh {
  padding: 0.5rem 0.75rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #94A3B8;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-refresh:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.form-row {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  min-width: 120px;
}

.form-group.flex-2 {
  flex: 2;
  min-width: 200px;
}

.form-group label {
  font-size: 0.75rem;
  color: #94A3B8;
  font-weight: 500;
}

.text-input,
.number-input {
  padding: 0.5rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.2);
  color: #fff;
  font-size: 0.9rem;
}

.text-input:focus,
.number-input:focus {
  outline: none;
  border-color: #20B4FF;
}

.number-input {
  width: 100%;
}

/* Rich Editor */
.rich-editor-toolbar {
  display: flex;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
}

.rich-editor-toolbar button {
  padding: 0.35rem 0.6rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #94A3B8;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.2s;
}

.rich-editor-toolbar button:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.rich-editor {
  min-height: 100px;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.2);
  color: #fff;
  font-size: 0.9rem;
  line-height: 1.5;
}

.rich-editor:focus {
  outline: none;
  border-color: #20B4FF;
}

.rich-editor:empty:before {
  content: 'Enter description...';
  color: #64748B;
}

/* Card Actions */
.card-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.btn-save, .btn-cancel, .btn-delete, .btn-activate, .btn-deactivate, .btn-sync {
  padding: 0.4rem 0.75rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.15s;
}

.btn-save {
  background: #28a745;
  color: #fff;
}

.btn-save:hover {
  background: #218838;
}

.btn-cancel {
  background: #3a3f47;
  color: #c9d1d9;
}

.btn-cancel:hover {
  background: #4a5058;
  color: #fff;
}

.btn-delete {
  background: transparent;
  color: #adb5bd;
}

.btn-delete:hover {
  background: #dc3545;
  color: #fff;
}

.btn-activate {
  background: rgba(40, 167, 69, 0.2);
  color: #28a745;
}

.btn-activate:hover {
  background: #28a745;
  color: #fff;
}

.btn-deactivate {
  background: rgba(108, 117, 125, 0.2);
  color: #94A3B8;
}

.btn-deactivate:hover {
  background: #6c757d;
  color: #fff;
}

.btn-sync {
  background: rgba(255, 255, 255, 0.05);
  color: #94A3B8;
}

.btn-sync:hover {
  background: #20B4FF;
  color: #fff;
}

/* Empty state */
.empty-state {
  text-align: center;
  padding: 3rem;
  color: #9ca3af;
}

.empty-state p {
  margin-bottom: 1rem;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.confirm-modal {
  background: #2a2e35;
  border-radius: 8px;
  padding: 1.5rem;
  max-width: 400px;
  width: 90%;
  border: 1px solid #3a3f47;
}

.confirm-modal h3 {
  margin: 0 0 0.75rem;
  color: #fff;
  font-size: 1.1rem;
}

.confirm-modal p {
  color: #c9d1d9;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.confirm-modal .warning {
  color: #dc3545;
  font-size: 0.8rem;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.25rem;
}

/* Transitions */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.list-enter-active,
.list-leave-active {
  transition: all 0.2s ease;
}

.list-enter,
.list-leave-to {
  opacity: 0;
}

.list-move {
  transition: transform 0.2s ease;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.15s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .currency-tabs {
    flex-direction: column;
    align-items: stretch;
  }

  .tab-actions {
    margin-left: 0;
    margin-top: 0.75rem;
    justify-content: space-between;
  }

  .offer-header {
    flex-direction: column;
    gap: 0.5rem;
  }

  .form-row {
    flex-direction: column;
  }

  .stripe-selector {
    flex-direction: column;
  }

  .card-actions {
    flex-wrap: wrap;
  }
}
</style>
